import React from 'react';
import { motion } from 'framer-motion';

interface NextStep {
  id: string;
  title: string;
  description: string;
  priority: 'High' | 'Medium' | 'Low';
  category: 'Core Features' | 'UX/UI' | 'Performance' | 'Marketing' | 'Technical' | 'Growth';
  estimatedTime: string;
  impact: 'High' | 'Medium' | 'Low';
  icon: string;
}

export const NextSteps: React.FC = () => {
  const nextSteps: NextStep[] = [
    {
      id: '1',
      title: 'Real Goal Progress Tracking',
      description: 'Connect the GoalYet interface to actual goal data - user goals, progress tracking, streak calculation, and milestone celebrations.',
      priority: 'High',
      category: 'Core Features',
      estimatedTime: '2-3 hours',
      impact: 'High',
      icon: '🎯'
    },
    {
      id: '2',
      title: 'Goal Creation & Management Flow',
      description: 'Build the complete goal lifecycle - create, edit, archive, and transition goals through states (active → completed → eternal).',
      priority: 'High',
      category: 'Core Features',
      estimatedTime: '3-4 hours',
      impact: 'High',
      icon: '📝'
    },
    {
      id: '3',
      title: 'AI Goal Coaching Integration',
      description: 'Connect the AI assistant to provide personalized coaching based on user goals, progress patterns, and motivational strategies.',
      priority: 'High',
      category: 'Core Features',
      estimatedTime: '2-3 hours',
      impact: 'High',
      icon: '🤖'
    },
    {
      id: '4',
      title: 'Mobile App Experience',
      description: 'Optimize the interface specifically for mobile usage - touch interactions, simplified flows, and mobile-first design patterns.',
      priority: 'Medium',
      category: 'UX/UI',
      estimatedTime: '4-6 hours',
      impact: 'High',
      icon: '📱'
    },
    {
      id: '5',
      title: 'Goal Sharing & Social Features',
      description: 'Allow users to share goals publicly, create accountability partnerships, and celebrate achievements with others.',
      priority: 'Medium',
      category: 'Growth',
      estimatedTime: '3-4 hours',
      impact: 'Medium',
      icon: '🤝'
    },
    {
      id: '6',
      title: 'Analytics & Insights Dashboard',
      description: 'Build user analytics showing goal completion patterns, streak analysis, and personalized insights for better goal achievement.',
      priority: 'Medium',
      category: 'Core Features',
      estimatedTime: '3-5 hours',
      impact: 'Medium',
      icon: '📊'
    },
    {
      id: '7',
      title: 'Notification & Reminder System',
      description: 'Implement smart notifications for goal check-ins, streak maintenance, and motivational nudges via email and browser notifications.',
      priority: 'Medium',
      category: 'Growth',
      estimatedTime: '2-3 hours',
      impact: 'High',
      icon: '🔔'
    },
    {
      id: '8',
      title: 'Goal Templates & Quick Start',
      description: 'Create pre-built goal templates for common objectives (fitness, career, learning) with proven step-by-step guidance.',
      priority: 'Medium',
      category: 'UX/UI',
      estimatedTime: '2-4 hours',
      impact: 'Medium',
      icon: '📋'
    },
    {
      id: '9',
      title: 'Performance Optimization',
      description: 'Optimize loading times, implement caching strategies, and ensure smooth animations across all devices and connection speeds.',
      priority: 'Low',
      category: 'Performance',
      estimatedTime: '3-4 hours',
      impact: 'Medium',
      icon: '⚡'
    },
    {
      id: '10',
      title: 'Marketing Website & SEO',
      description: 'Create dedicated marketing pages, implement SEO optimization, and build content strategy to attract users organically.',
      priority: 'Low',
      category: 'Marketing',
      estimatedTime: '4-6 hours',
      impact: 'High',
      icon: '🚀'
    },
    {
      id: '11',
      title: 'Data Export & Backup',
      description: 'Allow users to export their goal data, create backups, and ensure data portability for long-term goal tracking.',
      priority: 'Low',
      category: 'Technical',
      estimatedTime: '2-3 hours',
      impact: 'Low',
      icon: '💾'
    },
    {
      id: '12',
      title: 'Integration Ecosystem',
      description: 'Build integrations with calendar apps, fitness trackers, project management tools, and other productivity systems.',
      priority: 'Low',
      category: 'Growth',
      estimatedTime: '6-8 hours',
      impact: 'High',
      icon: '🔗'
    }
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-700 border-red-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'Low': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Core Features': return 'bg-orange-100 text-orange-700';
      case 'UX/UI': return 'bg-teal-100 text-teal-700';
      case 'Performance': return 'bg-purple-100 text-purple-700';
      case 'Marketing': return 'bg-pink-100 text-pink-700';
      case 'Technical': return 'bg-blue-100 text-blue-700';
      case 'Growth': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const priorityOrder = { 'High': 0, 'Medium': 1, 'Low': 2 };
  const sortedSteps = [...nextSteps].sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">🚀 Next Steps for GoalYet</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Continuing our relentless progress toward building the world's most effective goal achievement platform.
          Here's what we can tackle tomorrow to keep the momentum going.
        </p>
      </div>

      {/* Progress Summary */}
      <div className="bg-gradient-to-r from-orange-500 to-teal-500 text-white rounded-xl p-6">
        <div className="grid md:grid-cols-3 gap-6 text-center">
          <div>
            <div className="text-3xl font-bold">✅</div>
            <div className="text-2xl font-bold">Design System</div>
            <div className="text-sm opacity-90">Complete visual rebrand finished</div>
          </div>
          <div>
            <div className="text-3xl font-bold">🎯</div>
            <div className="text-2xl font-bold">12 Next Steps</div>
            <div className="text-sm opacity-90">Prioritized roadmap ready</div>
          </div>
          <div>
            <div className="text-3xl font-bold">⚡</div>
            <div className="text-2xl font-bold">High Momentum</div>
            <div className="text-sm opacity-90">Ready for tomorrow's sprint</div>
          </div>
        </div>
      </div>

      {/* Next Steps Grid */}
      <div className="grid gap-6">
        {sortedSteps.map((step, index) => (
          <motion.div
            key={step.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">{step.icon}</span>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{step.title}</h3>
                  <p className="text-gray-600 mt-1">{step.description}</p>
                </div>
              </div>
              <div className="flex flex-col items-end space-y-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(step.priority)}`}>
                  {step.priority} Priority
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(step.category)}`}>
                  {step.category}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm text-gray-500">
              <div className="flex items-center space-x-4">
                <span>⏱ {step.estimatedTime}</span>
                <span>📈 {step.impact} Impact</span>
              </div>
              <button className="text-orange-600 hover:text-orange-700 font-medium">
                Start Tomorrow →
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Action Plan */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
        <h2 className="text-2xl font-bold text-orange-900 mb-4">🎯 Recommended Tomorrow Focus</h2>
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
            <span><strong>Real Goal Progress Tracking</strong> - Connect the interface to actual user data</span>
          </div>
          <div className="flex items-center space-x-3">
            <span className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
            <span><strong>Goal Creation Flow</strong> - Build the complete goal lifecycle management</span>
          </div>
          <div className="flex items-center space-x-3">
            <span className="w-8 h-8 bg-orange-400 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
            <span><strong>AI Integration</strong> - Connect coaching to real goal data and progress</span>
          </div>
        </div>
        <div className="mt-4 p-3 bg-white rounded-lg border border-orange-200">
          <p className="text-sm text-orange-800">
            <strong>Success Criteria:</strong> By end of tomorrow, users should be able to create real goals,
            track actual progress, and receive personalized AI coaching based on their achievement patterns.
          </p>
        </div>
      </div>

      {/* Momentum Quote */}
      <div className="text-center py-8">
        <blockquote className="text-2xl font-medium text-gray-700 italic">
          {"Progress > Perfection • Momentum > Magnitude • Action > Planning"}
        </blockquote>
        <p className="text-gray-500 mt-2">— GoalYet Philosophy</p>
      </div>
    </div>
  );
};